import React, { useCallback, useMemo, useState } from 'react';
import { DayPicker as ReactDayPicker } from 'react-day-picker';
import type { DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import { useIntl } from 'react-intl';

import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { usePlaceholderFormat } from '~/i18n/hooks/useFormatDate/usePlaceholderFormat';
import { getFormattingLocale } from '~/i18n/utils';

import { Input } from '../../../types/Input';
import { Box } from '../../Box/Box';
import { ContentContainer } from '../../ContentContainer/ContentContainer';
import { Dropdown } from '../../Dropdown/Dropdown';
import { DropdownBody } from '../../Dropdown/subcomponents/DropdownBody';
import { Layout } from '../../Layout/Layout';
import { CommonProps } from '../types';
import { Caption } from './Caption';
import { Day } from './Day';
import * as formatters from './formatters';
import * as icuformatters from './icuformatters';
import { InputTrigger } from './InputTrigger';
import { getDateFnsLocale, getDisabledMatcher, getMonth } from './utils';
import { ViewProvider } from './ViewContext';

type Rangible<R extends boolean> = R extends true
  ? Input<DateRange>
  : Input<Date>;

type Props<R extends boolean> = Rangible<R> &
  CommonProps & {
    isLoading?: R;
    isRange?: R;
  };

export const DayPicker = <R extends boolean>({
  disabled,
  disabledDays,
  disabledDates,
  enableControl = true,
  error,
  format,
  id,
  isEmbedded,
  isLoading,
  isRange,
  max,
  min,
  month,
  name,
  placeholder: initialPlaceholder,
  readOnly,
  value,
  width,
  onChange,
  getDayTooltip,
}: Props<R>) => {
  const intl = useIntl();
  const hasLocalization = useFlag(FlagType.LanguageSelect);
  const getPlaceholderPattern = usePlaceholderFormat();
  const storedLocale = getFormattingLocale();

  const placeholder = hasLocalization
    ? getPlaceholderPattern('input_date')
    : initialPlaceholder;

  const locale = hasLocalization ? getDateFnsLocale(storedLocale) : undefined;

  const [displayMonth, setDisplayMonth] = useState<Date | undefined>(
    month || getMonth(value, isRange),
  );

  const onMonthNavigate = useCallback((date: Date) => {
    setDisplayMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  }, []);

  const icuFormatters = {
    formatCaption: (date: Date) =>
      icuformatters.formatCaption(date, { locale: storedLocale }),
    formatMonthCaption: (date: Date) =>
      icuformatters.formatMonthCaption(date, { locale: storedLocale }),
    formatDay: (date: Date) =>
      icuformatters.formatDay(date, { locale: storedLocale }),
    formatWeekdayName: (date: Date) =>
      icuformatters.formatWeekdayName(date, { locale: storedLocale }),
    formatYearCaption: (date: Date) =>
      icuformatters.formatYearCaption(date, { locale: storedLocale }),
    formatWeekNumber: (weekNumber: number) => weekNumber.toString(),
  };

  const handleChange = useCallback(
    (updatedDate: Date | DateRange | undefined) => {
      onChange((updatedDate as Date & DateRange) || null);
    },
    [onChange],
  );

  const components = useMemo(
    () => ({
      Caption: (props: any) => (
        <Caption {...props} onNavigateToMonth={onMonthNavigate} />
      ),
      Day: (props: React.ComponentProps<typeof Day>) => (
        <Day {...props} getDayTooltip={getDayTooltip} />
      ),
    }),
    [onMonthNavigate, getDayTooltip],
  );

  const dayPicker = (
    <ViewProvider>
      <Box readOnly={readOnly}>
        <ReactDayPicker
          disabled={getDisabledMatcher({
            disabled,
            disabledDays,
            disabledDates,
            max,
            min,
          })}
          components={components}
          defaultMonth={
            hasLocalization ? displayMonth : getMonth(value, isRange)
          }
          formatters={hasLocalization ? icuFormatters : formatters}
          locale={locale}
          mode={isRange ? 'range' : 'single'}
          month={hasLocalization ? displayMonth : month}
          selected={value || undefined}
          onSelect={handleChange}
        />
      </Box>
    </ViewProvider>
  );

  const triggerProps = {
    disabled,
    id,
    name,
    error,
    width,
    format,
    min,
    max,
    onMonthNavigate,
  };

  let control;
  if (isRange) {
    control = (
      <Layout spacing={2}>
        <InputTrigger
          {...triggerProps}
          name={`${name}-start`}
          placeholder={intl.formatMessage({
            id: 'DateInput.dayPicker.startDatePlaceholder',
            defaultMessage: 'Start Date',
            description: 'Placeholder for start date input',
          })}
          value={(value as DateRange)?.from || null}
          width="50%"
          onChange={
            hasLocalization
              ? (date) => {
                  const currentRange = value as DateRange;
                  handleChange({
                    from: date || undefined,
                    to: currentRange?.to || undefined,
                  });
                }
              : undefined
          }
        />
        <InputTrigger
          {...triggerProps}
          name={`${name}-end`}
          placeholder={intl.formatMessage({
            id: 'DateInput.dayPicker.endDatePlaceholder',
            defaultMessage: 'End Date',
            description: 'Placeholder for end date input',
          })}
          value={(value as DateRange)?.to || null}
          width="50%"
          onChange={
            hasLocalization
              ? (date) => {
                  const currentRange = value as DateRange;
                  handleChange({
                    from: currentRange?.from || undefined,
                    to: date || undefined,
                  });
                }
              : undefined
          }
        />
      </Layout>
    );
  } else {
    control = (
      <InputTrigger
        {...triggerProps}
        placeholder={placeholder}
        value={value as Date}
        onChange={
          hasLocalization
            ? (date) => {
                handleChange(date || undefined);
              }
            : undefined
        }
      />
    );
  }

  if (isEmbedded) {
    return enableControl ? (
      <Layout direction="column" spacing={4} w="min-content">
        {control}
        {dayPicker}
      </Layout>
    ) : (
      dayPicker
    );
  }

  if (disabled || readOnly) {
    return control;
  }

  return (
    <Dropdown trigger={control} triggerMode="triggerWidth" triggerWidth={width}>
      <DropdownBody>
        <ContentContainer loadingContent={{ isLoading }}>
          {dayPicker}
        </ContentContainer>
      </DropdownBody>
    </Dropdown>
  );
};
